/**
 * AccessibilityAdminPanel — embedded inside AdminEventPage.
 *
 * Responsibilities:
 *  - Toggle the service on/off per event
 *  - Choose the languages offered to delegates
 *  - Start / stop a live captioning session
 *  - Hand the speaker a "Start mic" link (carries the publish_token)
 *  - Show the QR code that delegates scan
 *  - List past sessions with export links (TXT / SRT / VTT)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AccessibilityIcon, Mic, Square, QrCode, ExternalLink, Loader2,
  Download, FileText, Captions, Volume2,
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

// iter-438k · bug#4 root fix: was reading the wrong localStorage keys
// ('auth' / 'token') so every request went unauthenticated → 401 → the
// user saw "save failed" no matter what they picked. Now uses the
// shared getToken() helper that knows about the Zustand-persisted store
// and legacy fallbacks (eventflow-auth → eventflow_token → token).
const authHeaders = () => {
  const tok = getToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const api = async (path, opts = {}) => {
  const r = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    // iter-438k bug#4 fix: extract the human-readable `detail` field from
    // FastAPI errors so the toast doesn't show raw JSON to users.
    let msg = '';
    try {
      const body = await r.text();
      if (body) {
        try {
          const parsed = JSON.parse(body);
          if (typeof parsed === 'string') {
            msg = parsed;
          } else if (parsed && typeof parsed === 'object') {
            const d = parsed.detail ?? parsed.message ?? parsed.error;
            if (typeof d === 'string') msg = d;
            else if (Array.isArray(d)) msg = d.map(x => x?.msg || x).join(', ');
            else msg = body;
          } else msg = body;
        } catch { msg = body; }
      }
    } catch { /* body already consumed */ }
    throw new Error(msg || `Request failed (${r.status})`);
  }
  return r.json();
};

const AccessibilityAdminPanel = ({ event }) => {
  const eventId = event?.id || event?._id;
  const qc = useQueryClient();

  const { data: catalog } = useQuery({
    queryKey: ['accessibility-languages'],
    queryFn: () => api('/accessibility/languages'),
    staleTime: 60_000,
  });
  const { data: config, isLoading: cfgLoading } = useQuery({
    queryKey: ['accessibility-config', eventId],
    queryFn: () => api(`/accessibility/events/${eventId}/config`),
    enabled: !!eventId,
  });
  const { data: sessions = [], refetch: refetchSessions } = useQuery({
    queryKey: ['accessibility-sessions', eventId],
    queryFn: () => api(`/accessibility/events/${eventId}/sessions`),
    enabled: !!eventId,
    refetchInterval: 5000,
  });

  // Local editable form state — initialised from config when it loads.
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (!config || form) return;
    setForm({
      enabled: !!config.enabled,
      source_lang: config.source_lang || 'en',
      target_langs: config.target_langs?.length ? config.target_langs : ['en'],
      tts_enabled: !!config.tts_enabled,
      tts_voice: config.tts_voice || 'alloy',
      captions_default: config.captions_default !== false,
      audio_default: !!config.audio_default,
      font_size_default: Number(config.font_size_default || 24),
      contrast_mode_default: config.contrast_mode_default || 'normal',
    });
  }, [config, form]);

  const saveCfg = useMutation({
    mutationFn: (body) => api(`/accessibility/events/${eventId}/config`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.success('Accessibility settings saved');
      qc.invalidateQueries({ queryKey: ['accessibility-config', eventId] });
    },
    onError: (e) => toast.error(e.message),
  });

  // iter-438k bug#4 fix: strip any codes the catalog doesn't know about
  // (schema drift from earlier deployments would otherwise 400 the save
  // and leave the user with a broken form). We also guarantee at least
  // one target language so `Field(default_factory=lambda: ["en"])` on
  // the pydantic side stays satisfied.
  const submitConfig = () => {
    if (!form) return;
    const validCodes = new Set((catalog?.languages || []).map((l) => l.code));
    const clean = { ...form };
    // Only sanitise when the catalog has already loaded (avoid nuking
    // legit codes if the fetch is still in flight).
    if (validCodes.size) {
      if (!validCodes.has(clean.source_lang)) clean.source_lang = 'en';
      clean.target_langs = (clean.target_langs || []).filter((c) => validCodes.has(c));
      if (!clean.target_langs.length) clean.target_langs = [clean.source_lang || 'en'];
    }
    saveCfg.mutate(clean);
  };

  const startSession = useMutation({
    mutationFn: (body) => api(`/accessibility/events/${eventId}/sessions`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { toast.success('Session started'); refetchSessions(); },
    onError: (e) => toast.error(e.message),
  });

  const stopSession = useMutation({
    mutationFn: (sid) => api(`/accessibility/sessions/${sid}?action=stop`, { method: 'PATCH' }),
    onSuccess: () => { toast.success('Session stopped'); refetchSessions(); },
    onError: (e) => toast.error(e.message),
  });

  const toggleLang = (code) => {
    setForm(f => {
      const has = f.target_langs.includes(code);
      const next = has ? f.target_langs.filter(c => c !== code) : [...f.target_langs, code];
      return { ...f, target_langs: next.length ? next : ['en'] };
    });
  };

  const active = useMemo(() => sessions.find(s => s.status === 'active'), [sessions]);
  const qrUrl = eventId ? `${API}/api/accessibility/events/${eventId}/qr.png?size=420` : null;
  const delegateUrl = eventId ? `${window.location.origin}/event/${eventId}/accessibility` : '';

  if (cfgLoading || !form) {
    return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="accessibility-admin-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <AccessibilityIcon className="h-5 w-5" /> Accessibility & live captions
        </h2>
        <p className="text-sm text-slate-500">
          Provide real-time captions, translation and (optional) translated audio so deaf, hard-of-hearing
          and non-English delegates can follow live sessions on their own phones.
        </p>
      </div>

      {/* Configuration card */}
      <Card>
        <CardHeader><CardTitle className="text-base">Configuration</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Enable for this event</Label>
              <p className="text-xs text-slate-500">When off, the delegate portal shows a &ldquo;not enabled&rdquo; message.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))}
                    data-testid="accessibility-enabled-switch" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Speaker language (source)</Label>
              <Select value={form.source_lang} onValueChange={(v) => setForm(f => ({ ...f, source_lang: v }))}>
                <SelectTrigger data-testid="accessibility-source-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog?.languages || []).map(l => (
                    <SelectItem key={l.code} value={l.code} disabled={!l.whisper}>
                      {l.native} — {l.name} {!l.whisper && <span className="text-xs text-slate-400">(translation only)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Whisper transcribes the speaker in this language.</p>
            </div>
            <div>
              <Label className="text-sm">TTS voice (for spoken translations)</Label>
              <Select value={form.tts_voice} onValueChange={(v) => setForm(f => ({ ...f, tts_voice: v }))}>
                <SelectTrigger data-testid="accessibility-tts-voice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'].map(v =>
                    <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm">Languages offered to delegates</Label>
            <p className="text-xs text-slate-500 mb-2">Pick every language captions should be translated into.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto p-2 border rounded">
              {(catalog?.languages || []).map(l => (
                <label key={l.code}
                       className="flex items-center gap-2 cursor-pointer text-sm"
                       data-testid={`accessibility-target-lang-${l.code}`}>
                  <Checkbox checked={form.target_langs.includes(l.code)}
                            onCheckedChange={() => toggleLang(l.code)} />
                  <span className="font-medium">{l.native}</span>
                  <span className="text-xs text-slate-400">{l.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between border rounded p-3">
              <div>
                <Label className="text-xs">Audio translation (TTS)</Label>
                <p className="text-[11px] text-slate-500">Lets delegates listen instead of read.</p>
              </div>
              <Switch checked={form.tts_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, tts_enabled: v }))}
                      data-testid="accessibility-tts-switch" />
            </div>
            <div className="border rounded p-3 space-y-1">
              <Label className="text-xs">Default font size (px)</Label>
              <Input type="number" min={14} max={56}
                     value={form.font_size_default}
                     onChange={(e) => setForm(f => ({ ...f, font_size_default: Number(e.target.value) }))}
                     data-testid="accessibility-font-default" />
            </div>
            <div className="border rounded p-3 space-y-1">
              <Label className="text-xs">Default contrast</Label>
              <Select value={form.contrast_mode_default} onValueChange={(v) => setForm(f => ({ ...f, contrast_mode_default: v }))}>
                <SelectTrigger data-testid="accessibility-contrast-default"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High contrast</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={submitConfig} disabled={saveCfg.isPending}
                    data-testid="accessibility-save-config">
              {saveCfg.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live session card */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Captions className="h-4 w-4" /> Live session</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {active ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <Badge className="bg-emerald-600 mr-2">LIVE</Badge>
                  <span className="font-medium">{active.label}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    started {new Date(active.started_at).toLocaleTimeString()} · {active.segment_count || 0} segments
                  </span>
                </div>
                <Button variant="destructive" size="sm" onClick={() => stopSession.mutate(active.id)}
                        data-testid="accessibility-stop-session">
                  <Square className="h-4 w-4 mr-1" /> Stop session
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button asChild size="sm" variant="outline">
                  <a href={`/event/${eventId}/accessibility/source/${active.id}?token=${encodeURIComponent(active.publish_token)}&event=${eventId}`}
                     target="_blank" rel="noreferrer" data-testid="accessibility-open-source">
                    <Mic className="h-4 w-4 mr-1" /> Open speaker microphone
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <a href={`/event/${eventId}/accessibility/display?lang=${active.source_lang || 'en'}`}
                     target="_blank" rel="noreferrer" data-testid="accessibility-open-display">
                    <ExternalLink className="h-4 w-4 mr-1" /> Open big-screen display
                  </a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-slate-500">No active session.</p>
              <Button onClick={() => startSession.mutate({ label: 'Live captions' })}
                      disabled={!form.enabled || startSession.isPending}
                      data-testid="accessibility-start-session">
                {startSession.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                Start session
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delegate access + QR */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><QrCode className="h-4 w-4" /> Delegate access</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Print this QR or display it on a side screen. Delegates scan it to open the captions on their phone — no app install required.
          </p>
          <div className="flex items-start gap-4 flex-wrap">
            {qrUrl && (
              <img src={qrUrl} alt="Accessibility QR" className="border rounded bg-white p-2"
                   width={220} height={220} data-testid="accessibility-qr-img" />
            )}
            <div className="space-y-2 text-sm">
              <div>
                <Label className="text-xs">Direct link</Label>
                <Input readOnly value={delegateUrl} onClick={(e) => e.target.select()}
                       data-testid="accessibility-delegate-url" />
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={delegateUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1" /> Preview as delegate
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Past sessions + exports */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Past sessions</CardTitle></CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <p className="text-sm text-slate-500">No sessions yet.</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {sessions.map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded border" data-testid={`accessibility-session-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{s.label || 'Session'}</p>
                    <p className="text-xs text-slate-500">
                      {s.started_at && new Date(s.started_at).toLocaleString()}
                      {s.ended_at && ` → ${new Date(s.ended_at).toLocaleTimeString()}`}
                      {' · '}{s.segment_count || 0} segments
                      {' · '}<span className="capitalize">{s.status}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {['txt', 'srt', 'vtt'].map(fmt => (
                      <Button key={fmt} variant="ghost" size="sm" asChild>
                        <a href={`${API}/api/accessibility/sessions/${s.id}/export.${fmt}?lang=${s.source_lang || 'en'}`}
                           target="_blank" rel="noreferrer"
                           data-testid={`accessibility-export-${fmt}-${s.id}`}>
                          <Download className="h-3 w-3 mr-1" /> .{fmt}
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {form.tts_enabled && (
        <p className="text-[11px] text-slate-500 flex items-center gap-1">
          <Volume2 className="h-3 w-3" /> Audio translation is on. Delegates can toggle &ldquo;Spoken audio&rdquo; in their portal.
        </p>
      )}
    </div>
  );
};

export default AccessibilityAdminPanel;
